import { API } from '@/core/Api';
import { getVerifiedToken } from '@/core/U';
interface StreamHandler {
  onTextChunk?: (text: string) => void;
  onImageGenerated?: (images: any[]) => void;
  onImageGenerationStarted?: () => void;
  onReasoningUpdate?: (reasoning: any) => void;
  onMessageUpdate?: (message: any) => void;
  onComplete?: (finalResult: StreamResult) => void;
  onError?: (error: Error) => void;
}

// 스트리밍 결과 타입
interface StreamResult {
  text: string;
  images: any[];
  reasoning?: any[];
  messages?: any[];
  rawResponse?: any;
}
export class GeminiAI {
  /**
   * OpenAI API 스트리밍 응답을 처리하는 공통 함수
   * @param endpoint API 엔드포인트 (상대 경로)
   * @param requestData 요청 데이터
   * @param handlers 스트리밍 이벤트 핸들러들
   * @returns 최종 스트리밍 결과
   */
  private async streamOpenAIResponse(
    endpoint: string,
    requestData: any,
    handlers: StreamHandler,
  ): Promise<StreamResult> {
    // 인증 토큰 가져오기
    const token = await getVerifiedToken();

    // 결과 수집용 변수들
    let accumulatedText = '';
    const allImages: any[] = [];
    const allReasoning: any[] = [];
    const allMessages: any[] = [];
    let rawResponse: any = null;

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_SERVER}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ ...requestData, stream: true }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenAI API 오류: ${response.statusText} - ${errorText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Response body is null');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        const lines = buffer.split('\n');

        // 마지막 불완전한 라인은 버퍼에 보관
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();

          // 이벤트 라인 건너뛰기
          if (trimmedLine.startsWith('event: ')) {
            continue;
          }

          if (trimmedLine.startsWith('data: ')) {
            const data = trimmedLine.slice(6).trim();

            // 종료 신호 확인
            if (data === '[DONE]') {
              const finalResult: StreamResult = {
                text: accumulatedText,
                images: allImages,
                reasoning: allReasoning,
                messages: allMessages,
                rawResponse,
              };
              handlers.onComplete?.(finalResult);
              return finalResult;
            }

            // 빈 데이터 건너뛰기
            if (!data) continue;

            try {
              const parsed = JSON.parse(data);
              rawResponse = parsed;

              // 텍스트 콘텐츠 처리 (choices.delta.content)
              if (parsed.choices?.[0]?.delta?.content) {
                const content = parsed.choices[0].delta.content;
                accumulatedText += content;
                handlers.onTextChunk?.(content);
              }

              // 이미지 생성 결과 처리 (output 배열)
              if (parsed.output) {
                // 이미지 생성 시작 감지 (status: "in_progress")
                const imageInProgressOutputs = parsed.output.filter(
                  (item: any) =>
                    item.type === 'image_generation_call' && item.status === 'in_progress',
                );

                if (imageInProgressOutputs.length > 0) {
                  handlers.onImageGenerationStarted?.();
                }

                // 이미지 생성 완료 처리 (status: "completed")
                const imageOutputs = parsed.output.filter(
                  (item: any) =>
                    item.type === 'image_generation_call' && item.status === 'completed',
                );

                if (imageOutputs.length > 0) {
                  const processedImages = imageOutputs
                    .map((imageData: any) => ({
                      ...imageData,
                      dataUrl: imageData.result
                        ? `data:image/${imageData.output_format || 'png'};base64,${
                            imageData.result
                          }`
                        : null,
                    }))
                    .filter((img: any) => img.dataUrl);

                  // 중복 이미지 방지: 이미 존재하는 ID는 제외
                  const newImages = processedImages.filter(
                    (newImg: any) =>
                      !allImages.some((existingImg: any) => existingImg.id === newImg.id),
                  );

                  if (newImages.length > 0) {
                    console.log(
                      `🎨 Adding ${newImages.length} new images (filtered ${
                        processedImages.length - newImages.length
                      } duplicates)`,
                    );
                    allImages.push(...newImages);
                    handlers.onImageGenerated?.(newImages);
                  }
                }

                // 추론 데이터 처리
                const reasoningOutputs = parsed.output.filter(
                  (item: any) => item.type === 'reasoning',
                );
                if (reasoningOutputs.length > 0) {
                  allReasoning.push(...reasoningOutputs);
                  handlers.onReasoningUpdate?.(reasoningOutputs);
                }

                // 메시지 데이터 처리
                const messageOutputs = parsed.output.filter((item: any) => item.type === 'message');
                if (messageOutputs.length > 0) {
                  allMessages.push(...messageOutputs);
                  handlers.onMessageUpdate?.(messageOutputs);

                  // 메시지에서 텍스트 콘텐츠 추출
                  messageOutputs.forEach((msg: any) => {
                    if (msg.content && Array.isArray(msg.content)) {
                      msg.content.forEach((contentItem: any) => {
                        if (contentItem.type === 'output_text' && contentItem.text) {
                          //text 중복방지 -> TODO 보완필요
                          if (accumulatedText.length === 0) {
                            accumulatedText += contentItem.text;
                            handlers.onTextChunk?.(contentItem.text);
                          }
                        }
                      });
                    }
                  });
                }
              }

              // finish_reason 체크
              const finishReason = parsed.choices?.[0]?.finish_reason;
              if (finishReason) {
                const finalResult: StreamResult = {
                  text: accumulatedText,
                  images: allImages,
                  reasoning: allReasoning,
                  messages: allMessages,
                  rawResponse: parsed,
                };
                handlers.onComplete?.(finalResult);
                return finalResult;
              }
            } catch (error) {
              // JSON 파싱 에러가 발생해도 스트리밍 계속 진행
              if (data.length > 100) {
                console.warn('JSON parse error for large data chunk:', error);
              } else {
                console.warn('JSON parse error:', error, 'for data:', data);
              }
            }
          }
        }
      }

      reader.releaseLock();

      // 스트리밍 완료 후 최종 결과 반환
      const finalResult: StreamResult = {
        text: accumulatedText,
        images: allImages,
        reasoning: allReasoning,
        messages: allMessages,
        rawResponse,
      };
      handlers.onComplete?.(finalResult);
      return finalResult;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      handlers.onError?.(errorObj);
      throw errorObj;
    }
  }

  /**
   * Gemini 비전 스트리밍 - 이미지 생성기능도 존재
   * @param content 질문 내용
   * @param imageUrl 분석할 이미지 URL
   * @param onChunk 스트리밍 콜백
   * @param options 추가 옵션들
   */
  async imageGenerationModelWithStream(
    messages: any[],
    onChunk: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      detail?: 'low' | 'high' | 'auto';
      model?: string;
      onImageGenerated?: (imageData: any[]) => void;
      onImageGenerationStarted?: () => void;
      thinkingMode?: boolean;
      includeThoughts?: boolean;
    } = {},
  ): Promise<StreamResult> {
    const requestData = {
      model: options.model ?? 'gemini-2.5-flash', //gemini-2.5-flash-lite gemini-2.5-flash
      messages: messages,
      max_tokens: options.max_tokens || 4000,
      temperature: options.temperature || 1.0,
      thinkingMode: options.thinkingMode,
      includeThoughts: options.includeThoughts,
      stream: true,
    };

    // return this.streamOpenAIResponse('/api/ai/gemini/chat/completions', requestData, {
    //   onTextChunk: onChunk,
    //   onImageGenerated: options.onImageGenerated,
    //   onImageGenerationStarted: options.onImageGenerationStarted,
    //   onComplete: (result) => {
    //     console.log('✅ Streaming completed. Final results:', {
    //       text: result.text,
    //       images: result.images,
    //     });
    //   },
    //   onError: (error) => {
    //     console.error('OpenAI Vision Streaming error:', error);
    //     throw error;
    //   },
    // });

    return this.streamOpenAIResponse('/api/ai/gemini/chat/completions', requestData, {
      onTextChunk: onChunk,
      onImageGenerated: options.onImageGenerated,
      onImageGenerationStarted: options.onImageGenerationStarted,
      onComplete: (result) => {
        console.log('✅ Streaming completed. Final results:', {
          text: result.text,
          images: result.images,
        });
      },
      onError: (error) => {
        console.error('OpenAI Vision Streaming error:', error);
        throw error;
      },
    });
  }
}
